import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import apiManager from '../../../lib/api';
import trackingManager from '../../../lib/tracking';
import { EventType } from '../../../lib/tracking/eventTracker';
import { UserAddress } from '../../../lib/api/services/user';

interface Address extends UserAddress {}

const AddressesPage = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [formData, setFormData] = useState<Omit<Address, 'id'>>({
    type: 'shipping',
    default: false,
    name: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'España',
    phone: ''
  });
  
  const [formErrors, setFormErrors] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    phone: ''
  });

  // Cargar direcciones del usuario usando la API
  useEffect(() => {
    if (auth.user) {
      const fetchAddresses = async () => {
        setIsLoading(true);
        try {
          // Registrar evento de vista de página
          trackingManager.events.track(
            EventType.PAGE_VIEW,
            {
              path: window.location.pathname,
              pageTitle: document.title,
              customData: { section: 'addresses' }
            }
          );
          
          // Rastrear evento personalizado
          trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'view_addresses_page',
              userId: auth.user?.id
            }
          );
          
          // Obtener direcciones del usuario
          const response = await apiManager.users.getAddresses();
          
          if (response.success && response.data) {
            setAddresses(response.data);
          } else {
            console.error('Error al cargar direcciones:', response.error);
            setAddresses([]);
          }
        } catch (error) {
          console.error('Error al cargar direcciones:', error);
          setAddresses([]);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchAddresses();
    }
  }, [auth.user]);
  
  const resetForm = () => {
    setFormData({
      type: 'shipping',
      default: false,
      name: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'España',
      phone: ''
    });
    setFormErrors({
      name: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      phone: ''
    });
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Limpiar errores
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    
    // Limpiar mensaje de éxito
    if (successMessage) {
      setSuccessMessage('');
    }
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  
  const validateForm = () => {
    let isValid = true;
    const errors = {
      name: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      phone: ''
    };
    
    if (!formData.name.trim()) {
      errors.name = 'El nombre es obligatorio';
      isValid = false;
    }
    
    if (!formData.address.trim()) {
      errors.address = 'La dirección es obligatoria';
      isValid = false;
    }
    
    if (!formData.city.trim()) {
      errors.city = 'La ciudad es obligatoria';
      isValid = false;
    }
    
    if (!formData.postalCode.trim()) {
      errors.postalCode = 'El código postal es obligatorio';
      isValid = false;
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      errors.postalCode = 'El código postal debe tener 5 dígitos';
      isValid = false;
    }
    
    if (!formData.country.trim()) {
      errors.country = 'El país es obligatorio';
      isValid = false;
    }
    
    if (formData.phone && !/^\d{9}$/.test(formData.phone)) {
      errors.phone = 'El teléfono debe tener 9 dígitos';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleAddAddress = () => {
    // Registrar evento
    trackingManager.events.track(
      EventType.CUSTOM,
      {
        eventName: 'add_address_click',
        userId: auth.user?.id
      }
    );
    
    setIsAddingAddress(true);
    resetForm();
  };
  
  const handleEditAddress = (id: string) => {
    // Registrar evento
    trackingManager.events.track(
      EventType.CUSTOM,
      {
        eventName: 'edit_address_click',
        userId: auth.user?.id,
        addressId: id
      }
    );
    
    const addressToEdit = addresses.find(addr => addr.id === id);
    if (addressToEdit) {
      setFormData({ ...addressToEdit });
      setIsEditingAddress(id);
    }
  };
  
  const handleDeleteAddress = async (id: string) => {
    // Registrar evento
    trackingManager.events.track(
      EventType.CUSTOM,
      {
        eventName: 'delete_address_click',
        userId: auth.user?.id,
        addressId: id
      }
    );
    
    setIsSubmitting(true);
    
    try {
      // Llamar a la API para eliminar la dirección
      const response = await apiManager.users.deleteAddress(id);
      
      if (response.success) {
        // Eliminar la dirección del estado
        setAddresses(addresses.filter(addr => addr.id !== id));
        
        // Registrar evento de éxito
        trackingManager.events.track(
          EventType.CUSTOM,
          {
            eventName: 'delete_address_success',
            userId: auth.user?.id,
            addressId: id
          }
        );
        
        // Mostrar mensaje de éxito
        setSuccessMessage('La dirección ha sido eliminada correctamente');
      } else {
        console.error('Error al eliminar la dirección:', response.error);
      }
    } catch (error) {
      console.error('Error al eliminar la dirección:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    // Registrar evento
    trackingManager.events.track(
      EventType.CUSTOM,
      {
        eventName: 'cancel_address_form',
        userId: auth.user?.id,
        isEditing: isEditingAddress !== null
      }
    );
    
    setIsAddingAddress(false);
    setIsEditingAddress(null);
    resetForm();
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (isEditingAddress) {
        // Registrar evento de inicio de actualización
        trackingManager.events.track(
          EventType.CUSTOM,
          {
            eventName: 'update_address_submit',
            userId: auth.user?.id,
            addressId: isEditingAddress,
            addressType: formData.type
          }
        );
        
        // Actualizar dirección existente mediante la API
        const response = await apiManager.users.updateAddress(isEditingAddress, formData);
        
        if (response.success && response.data) {
          const updatedAddress = response.data;
          
          // Actualizar dirección en el estado de forma segura
          setAddresses(prev => 
            prev.map(addr => addr.id === isEditingAddress ? updatedAddress : addr)
          );
          
          // Registrar evento de éxito
          trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'update_address_success',
              userId: auth.user?.id,
              addressId: isEditingAddress,
              addressType: formData.type
            }
          );
          
          setSuccessMessage('La dirección ha sido actualizada correctamente');
          setIsEditingAddress(null);
        } else {
          console.error('Error al actualizar la dirección:', response.error);
        }
      } else {
        // Registrar evento de inicio de creación
        trackingManager.events.track(
          EventType.CUSTOM,
          {
            eventName: 'create_address_submit',
            userId: auth.user?.id,
            addressType: formData.type
          }
        );
        
        // Agregar nueva dirección mediante la API
        const response = await apiManager.users.addAddress(formData);
        
        if (response.success && response.data) {
          const newAddress = response.data;
          
          // Agregar la nueva dirección al estado de forma segura
          setAddresses(prev => [...prev, newAddress]);
          
          // Registrar evento de éxito
          trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'create_address_success',
              userId: auth.user?.id,
              addressId: newAddress.id,
              addressType: formData.type
            }
          );
          
          setSuccessMessage('La dirección ha sido añadida correctamente');
          setIsAddingAddress(false);
        } else {
          console.error('Error al crear la dirección:', response.error);
        }
      }
      
      resetForm();
    } catch (error) {
      console.error('Error al guardar la dirección:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }
  
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link 
            to="/account" 
            className="text-sm font-medium text-black hover:text-gray-700"
            onClick={() => trackingManager.events.track(
              EventType.CUSTOM,
              {
                eventName: 'addresses_back_to_account',
                userId: auth.user?.id
              }
            )}
          >
            ← Volver a mi cuenta
          </Link>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Mis direcciones</h1>
              <p className="mt-1 text-sm text-gray-500">Gestiona tus direcciones de envío y facturación.</p>
            </div>
            
            {!isAddingAddress && !isEditingAddress && (
              <button
                onClick={handleAddAddress}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
              >
                Añadir dirección
              </button>
            )}
          </div>
          
          <div className="px-6 py-6">
            {successMessage && (
              <div className="mb-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Formulario para añadir/editar dirección */}
            {(isAddingAddress || isEditingAddress) && (
              <form onSubmit={handleSubmit} className="mb-10">
                <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                  <div className="sm:col-span-2">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <input
                          id="shipping"
                          name="type"
                          type="radio"
                          value="shipping"
                          checked={formData.type === 'shipping'}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-black focus:ring-black border-gray-300"
                        />
                        <label htmlFor="shipping" className="ml-2 block text-sm font-medium text-gray-700">
                          Dirección de envío
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="billing"
                          name="type"
                          type="radio"
                          value="billing"
                          checked={formData.type === 'billing'}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-black focus:ring-black border-gray-300"
                        />
                        <label htmlFor="billing" className="ml-2 block text-sm font-medium text-gray-700">
                          Dirección de facturación
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <div className="flex items-center">
                      <input
                        id="default"
                        name="default"
                        type="checkbox"
                        checked={formData.default}
                        onChange={handleCheckboxChange}
                        className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                      />
                      <label htmlFor="default" className="ml-2 block text-sm font-medium text-gray-700">
                        Establecer como dirección predeterminada
                      </label>
                    </div>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Nombre completo
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        formErrors.name ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                    )}
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Dirección
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        formErrors.address ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    />
                    {formErrors.address && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      Ciudad
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        formErrors.city ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    />
                    {formErrors.city && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.city}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                      Código postal
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        formErrors.postalCode ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    />
                    {formErrors.postalCode && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.postalCode}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                      País
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm"
                    >
                      <option value="España">España</option>
                      <option value="Portugal">Portugal</option>
                      <option value="Francia">Francia</option>
                      <option value="Alemania">Alemania</option>
                      <option value="Italia">Italia</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Teléfono (opcional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        formErrors.phone ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    />
                    {formErrors.phone && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                    )}
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Guardando...
                      </>
                    ) : (
                      isEditingAddress ? 'Guardar cambios' : 'Añadir dirección'
                    )}
                  </button>
                </div>
              </form>
            )}
            
            {/* Lista de direcciones */}
            {!isAddingAddress && !isEditingAddress && (
              <>
                {addresses.length > 0 ? (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {addresses.map((address) => (
                      <div
                        key={address.id}
                        className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-150"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              address.type === 'shipping' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                            }`}>
                              {address.type === 'shipping' ? 'Envío' : 'Facturación'}
                            </span>
                            
                            {address.default && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Predeterminada
                              </span>
                            )}
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditAddress(address.id)}
                              className="text-gray-500 hover:text-gray-700 p-1"
                              title="Editar"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            
                            <button
                              onClick={() => handleDeleteAddress(address.id)}
                              className="text-gray-500 hover:text-red-600 p-1"
                              title="Eliminar"
                              disabled={isSubmitting}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-700 space-y-1">
                          <p className="font-medium">{address.name}</p>
                          <p>{address.address}</p>
                          <p>{address.postalCode}, {address.city}</p>
                          <p>{address.country}</p>
                          {address.phone && <p>Tel: {address.phone}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">No hay direcciones guardadas</h3>
                    <p className="mt-1 text-sm text-gray-500">Añade tu primera dirección para facilitar tus compras.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AddressesPage; 